<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, inject, watch, computed } from "vue"
import { useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import * as yup from "yup"
import { useFetchWrapper } from "@core/composables"
import type { Building, BuildingSourcePayload } from "@types"
import type { Services } from "@services"
import { useSourceStore } from "@core/stores/source.store"
import { storeToRefs } from "pinia"

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Bâtiment avec label formaté pour l'affichage dans BaseAutoComplete
 */
type BuildingWithLabel = Building & {
    label?: string
}

// ================================
// PROPS & EMITS
// ================================
/**
 * Type des valeurs du formulaire
 */
export type SensorPositionFormValue = {
    _id_building: string
    buildingName?: string // Nom du bâtiment (requis en mode update)
    start_date?: string
    end_date?: string
    description?: string
    floor?: string
}

interface Props {
    /**
     * Identifiant de la source
     */
    sourceId: string
    /**
     * Mode du formulaire : "add" pour ajout, "update" pour édition
     */
    mode?: "add" | "update"
    /**
     * Valeur du formulaire (v-model)
     */
    modelValue?: SensorPositionFormValue | null
}

const props = withDefaults(defineProps<Props>(), {
    mode: "add",
    modelValue: null,
})

interface Emits {
    /**
     * Émis lors de la mise à jour de la valeur du formulaire (v-model)
     * Émis uniquement lors du clic sur "Valider"
     * @param value - Valeurs du formulaire
     */
    (e: "update:modelValue", value: SensorPositionFormValue | null): void
    /**
     * Émis lors de l'annulation
     */
    (e: "cancel"): void
}

const emit = defineEmits<Emits>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            form: {
                building: "Bâtiment",
                building_required: "Le bâtiment est obligatoire",
                building_placeholder: "Rechercher un bâtiment...",
                building_hint: "Saisissez au moins 3 caractère.",
                start_date: "Date d'installation",
                start_date_invalid:
                    "La date d'installation doit être une date valide",
                start_date_before_end:
                    "La date d'installation doit être antérieure à la date de désinstallation",
                end_date: "Date de désinstallation",
                end_date_invalid:
                    "La date de désinstallation doit être une date valide",
                end_date_after_start:
                    "La date de désinstallation doit être postérieure à la date d'installation",
                description: "Description",
                floor: "Étage",
                floor_number: "L'étage doit être un nombre",
                floor_positive: "L'étage doit être supérieur ou égal à 0",
                validate: "Valider",
                cancel: "Annuler",
                error_add:
                    "Erreur lors de l'ajout de la position. Veuillez réessayer. Si le problème persiste, contactez notre support.",
                error_update:
                    "Erreur lors de la mise à jour de la position. Veuillez réessayer. Si le problème persiste, contactez notre support.",
                error_search: "Erreur lors de la recherche de bâtiments",
                loading: "Chargement...",
            },
        },
    },
})

// ================================
// COMPOSABLES & SERVICES
// ================================

const sourceStore = useSourceStore()

const { getSourceById } = storeToRefs(sourceStore)

const {
    isCreatingSensorPosition,
    isUpdatingSensorPosition,
    hasErrorCreatingSensorPosition,
    hasErrorUpdatingSensorPosition,
} = storeToRefs(sourceStore)

const services = inject<Services>("services")!

// ================================
// ÉTAT DES DONNÉES
// ================================
/**
 * Bâtiment sélectionné
 */
const selectedBuilding = ref<BuildingWithLabel | null>(null)

/**
 * Résultats de la recherche de bâtiments
 */
const results = ref<Building[]>([])

/**
 * Formate l'affichage d'un bâtiment : "Nom - Adresse"
 * @param building - Bâtiment à formater
 * @returns Chaîne formatée
 */
function formatBuildingLabel(building: Building): string {
    const name = building.name || ""
    const addressParts = building.address
        ? [
              building.address.street,
              building.address.city,
              building.address.zipcode,
          ]
              .filter(Boolean)
              .join(", ")
        : ""

    if (addressParts) {
        return `${name} - ${addressParts}`
    }
    return name
}

// ================================
// FETCH WRAPPERS
// ================================

/**
 * useFetchWrapper pour la recherche de bâtiments
 */
const {
    fetch: fetchBuildings,
    isFetching: isSearching,
    hasError: hasErrorSearch,
    error: errorSearch,
} = useFetchWrapper(async (query: string) => {
    const trimmedQuery = query.trim()

    if (!trimmedQuery || trimmedQuery.length < 2) {
        results.value = []
        return
    }

    let buildings = await services.$searchBuildings(trimmedQuery)

    const source = getSourceById.value(props.sourceId)
    if (source) {
        const sourceBuildings =
            source.buildings?.map((building) => building._id) ?? []
        // Retire de buildings les bâtiments déjà sélectionnés
        buildings = buildings.filter(
            (building) => !sourceBuildings.includes(building._id)
        )
    }

    // S'assurer que buildings est un tableau
    results.value = Array.isArray(buildings) ? buildings : []
})

/**
 * État de soumission combiné (ajout ou mise à jour)
 */
const isSubmitting = computed(
    () => isCreatingSensorPosition.value || isUpdatingSensorPosition.value
)

/**
 * Erreur combinée (ajout ou mise à jour)
 */
const hasErrorSubmit = computed(
    () =>
        hasErrorCreatingSensorPosition.value ||
        hasErrorUpdatingSensorPosition.value
)

// ================================
// VALIDATION
// ================================
const schema = yup.object({
    /**
     * _id_building : Requis, un bâtiment doit être sélectionné
     */
    _id_building: yup
        .string()
        .required(t("form.building_required"))
        .min(1, t("form.building_required")),
    /**
     * start_date : Optionnel, date valide, doit être avant end_date
     */
    start_date: yup
        .date()
        .nullable()
        .typeError(t("form.start_date_invalid"))
        .test(
            "start-before-end",
            t("form.start_date_before_end"),
            function (value) {
                if (!value || !this.parent.end_date) return true
                return value <= this.parent.end_date
            }
        ),
    /**
     * end_date : Optionnel, date valide, doit être après start_date
     */
    end_date: yup
        .date()
        .nullable()
        .typeError(t("form.end_date_invalid"))
        .test(
            "end-after-start",
            t("form.end_date_after_start"),
            function (value) {
                if (!value || !this.parent.start_date) return true
                return value >= this.parent.start_date
            }
        ),
    /**
     * description : Optionnel
     */
    description: yup.string().nullable(),
    /**
     * floor : Optionnel, nombre >= 0
     */
    floor: yup
        .string()
        .nullable()
        .test(
            "is-number",
            t("form.floor_number"),
            (value) => !value || !isNaN(Number(value))
        )
        .test(
            "is-positive",
            t("form.floor_positive"),
            (value) => !value || Number(value) >= 0
        ),
})

const { errors, handleSubmit, defineField, resetForm } = useForm<{
    _id_building: string
    start_date: Date | null
    end_date: Date | null
    description: string | null
    floor: string | null
}>({
    validationSchema: schema,
    initialValues: {
        _id_building: props.modelValue?._id_building || "",
        start_date: props.modelValue?.start_date
            ? new Date(props.modelValue.start_date)
            : null,
        end_date: props.modelValue?.end_date
            ? new Date(props.modelValue.end_date)
            : null,
        description: props.modelValue?.description || null,
        floor: props.modelValue?.floor || null,
    },
})

const [formBuildingId] = defineField("_id_building")
const [startDate] = defineField("start_date")
const [endDate] = defineField("end_date")
const [description] = defineField("description")
const [floor] = defineField("floor")

/**
 * Normalise une valeur de date (Date ou string) en objet Date
 * @param value - Valeur à normaliser
 * @returns Date normalisée ou null
 */
function normalizeDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null
    if (value instanceof Date) return value
    if (typeof value === "string") {
        const parsed = new Date(value)
        return !isNaN(parsed.getTime()) ? parsed : null
    }
    return null
}

// ================================
// RECHERCHE DE BÂTIMENT
// ================================
/**
 * Recherche avec debounce de 300ms
 */
const searchBuildings = useDebounceFn((event: { query: string }) => {
    fetchBuildings(event.query).catch(() => {
        // Erreur gérée par useFetchWrapper
        results.value = []
    })
}, 0)

/**
 * Résultats formatés pour l'AutoComplete avec label
 */
const formattedResults = computed(() => {
    if (!Array.isArray(results.value)) {
        return []
    }
    return results.value.map((building) => ({
        ...building,
        label: formatBuildingLabel(building),
    }))
})

/**
 * Synchronise formBuildingId avec selectedBuilding pour déclencher la validation
 */
watch(selectedBuilding, (newBuilding) => {
    if (newBuilding?._id) {
        formBuildingId.value = newBuilding._id
    } else {
        formBuildingId.value = ""
        results.value = []
    }
})

/**
 * Mode édition : détecte si on est en mode édition
 */
const isEditMode = computed(() => props.mode === "update")

// ================================
// INITIALISATION
// ================================
/**
 * Clé unique basée sur les props pour détecter les changements
 */
const formKey = computed(() => {
    return JSON.stringify(props.modelValue)
})

/**
 * Réinitialise le formulaire quand modelValue change
 */
watch(
    formKey,
    () => {
        if (!props.modelValue) {
            // Réinitialiser le formulaire si pas de modelValue
            resetForm({
                values: {
                    _id_building: "",
                    start_date: null,
                    end_date: null,
                    description: null,
                    floor: null,
                },
            })
            selectedBuilding.value = null
            formBuildingId.value = ""
            results.value = []
            return
        }

        // Réinitialiser le formulaire avec les valeurs de modelValue
        resetForm({
            values: {
                _id_building: props.modelValue._id_building || "",
                start_date: props.modelValue.start_date
                    ? new Date(props.modelValue.start_date)
                    : null,
                end_date: props.modelValue.end_date
                    ? new Date(props.modelValue.end_date)
                    : null,
                description: props.modelValue.description || null,
                floor: props.modelValue.floor || null,
            },
        })

        // Si on a un _id_building, initialiser le formulaire
        if (props.modelValue._id_building) {
            formBuildingId.value = props.modelValue._id_building
            // En mode update, on n'a pas besoin de selectedBuilding car on affiche un champ texte
            if (props.mode === "add") {
                selectedBuilding.value = null
            }
        } else {
            selectedBuilding.value = null
            formBuildingId.value = ""
            results.value = []
        }
    },
    { immediate: true }
)

// ================================
// SOUMISSION
// ================================
/**
 * Soumission du formulaire
 */
const onSubmit = handleSubmit(async (values) => {
    if (isSubmitting.value) return

    // Normaliser les dates pour s'assurer qu'elles sont des objets Date
    const normalizedStartDate = normalizeDate(values.start_date)
    const normalizedEndDate = normalizeDate(values.end_date)

    const payload = {
        sourceId: props.sourceId,
        buildingId: values._id_building,
        start_date: normalizedStartDate
            ? normalizedStartDate.toISOString().split("T")[0]
            : undefined,
        end_date: normalizedEndDate
            ? normalizedEndDate.toISOString().split("T")[0]
            : undefined,
        description: values.description || undefined,
        floor: values.floor || undefined,
    }

    // Émettre les valeurs du formulaire via v-model uniquement lors de la validation
    const formValue: SensorPositionFormValue = {
        _id_building: values._id_building,
        start_date: normalizedStartDate
            ? normalizedStartDate.toISOString().split("T")[0]
            : undefined,
        end_date: normalizedEndDate
            ? normalizedEndDate.toISOString().split("T")[0]
            : undefined,
        description: values.description || undefined,
        floor: values.floor || undefined,
    }

    if (isEditMode.value) {
        // Mise à jour
        await sourceStore.updateSensorPosition(payload as BuildingSourcePayload)
        if (!hasErrorUpdatingSensorPosition.value) {
            emit("update:modelValue", {
                ...formValue,
                buildingName: props.modelValue?.buildingName || "",
            })
        }
    } else {
        // Ajout
        await sourceStore.createSensorPosition(payload as BuildingSourcePayload)
        if (!hasErrorCreatingSensorPosition.value) {
            emit("update:modelValue", formValue)
        }
    }
})

/**
 * Annulation du formulaire
 */
function onCancel() {
    emit("cancel")
}
</script>

<template>
    <div class="sensor-position-form space-y-4 p-2">
        <!-- Message d'erreur lors de la recherche -->
        <BaseMessage
            v-if="hasErrorSearch"
            severity="error"
            variant="simple"
            size="small"
            class="mb-4"
        >
            <p>{{ t("form.error_search") }}: {{ errorSearch?.message }}</p>
        </BaseMessage>

        <!-- Recherche de bâtiment -->
        <BaseInputWrapper
            input-id="building-search"
            :label="t('form.building')"
            :error="errors._id_building"
            :hint="isEditMode ? undefined : t('form.building_hint')"
        >
            <!-- Mode édition : champ texte désactivé avec le nom du bâtiment -->
            <BaseInputText
                v-if="isEditMode"
                :model-value="props.modelValue?.buildingName || ''"
                :disabled="true"
                class="w-full"
            />

            <!-- Mode ajout : autocomplete pour rechercher un bâtiment -->
            <BaseAutoComplete
                v-else
                v-model="selectedBuilding"
                :suggestions="formattedResults"
                @complete="searchBuildings"
                :loading="isSearching"
                :placeholder="t('form.building_placeholder')"
                class="w-full"
                option-label="label"
                data-key="_id"
                show-clear
            >
                <template #option="slotProps">
                    <span>{{ slotProps.option.label }}</span>
                </template>
            </BaseAutoComplete>
        </BaseInputWrapper>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Date d'installation -->
            <div class="flex flex-col">
                <label
                    for="start-date"
                    class="text-gray-500 text-sm mb-2 truncate"
                >
                    {{ t("form.start_date") }}
                </label>
                <BaseDatePicker
                    id="start-date"
                    v-model="startDate"
                    date-format="dd/mm/yy"
                    show-icon
                    class="w-full"
                    :max-date="endDate ?? undefined"
                    :invalid="!!errors.start_date"
                />
                <BaseMessage
                    v-if="errors.start_date"
                    severity="error"
                    variant="simple"
                    size="small"
                    :pt="{
                        content: 'border-none!',
                    }"
                    class="mt-1"
                >
                    <p>{{ errors.start_date }}</p>
                </BaseMessage>
            </div>
            <!-- Date de désinstallation -->
            <div class="flex flex-col">
                <label
                    for="end-date"
                    class="text-gray-500 text-sm mb-2 truncate"
                >
                    {{ t("form.end_date") }}
                </label>
                <BaseDatePicker
                    id="end-date"
                    v-model="endDate"
                    date-format="dd/mm/yy"
                    show-icon
                    class="w-full"
                    :min-date="startDate ?? undefined"
                    :invalid="!!errors.end_date"
                />
                <BaseMessage
                    v-if="errors.end_date"
                    severity="error"
                    variant="simple"
                    size="small"
                    :pt="{
                        content: 'border-none!',
                    }"
                    class="mt-1"
                >
                    <p>{{ errors.end_date }}</p>
                </BaseMessage>
            </div>
        </div>

        <!-- Description -->
        <BaseInputWrapper
            input-id="description"
            :label="t('form.description')"
            :error="errors.description"
        >
            <BaseTextarea v-model="description" rows="3" class="w-full" />
        </BaseInputWrapper>

        <!-- Étage -->
        <BaseInputWrapper
            input-id="floor"
            :label="t('form.floor')"
            :error="errors.floor"
        >
            <BaseInputText v-model="floor" class="w-full" />
        </BaseInputWrapper>

        <!-- Boutons d'action -->
        <div class="flex justify-end gap-4 pt-4">
            <BaseButton
                :label="t('form.cancel')"
                severity="secondary"
                @click="onCancel"
            />
            <BaseButton
                :label="isSubmitting ? t('form.loading') : t('form.validate')"
                :loading="isSubmitting"
                :disabled="isSubmitting"
                @click="onSubmit"
            />
        </div>

        <!-- Message d'erreur lors de la soumission -->
        <BaseMessage
            v-if="hasErrorSubmit"
            severity="error"
            size="small"
            class="mb-4"
        >
            <p>
                {{ isEditMode ? t("form.error_update") : t("form.error_add") }}
            </p>
        </BaseMessage>
    </div>
</template>
